/**
 * The countdown to the date users must have set their new password by (ZEY-3140). The API sends the deadline as a
 * plain `YYYY-MM-DD` (the SetPassword announcement's payload `{ "deadline": … }`, and MyAccount's `deadline`); it runs
 * to the end of that day in the user's own time zone.
 */

export interface DeadlineNotice {
  /** Translation key of the line to show. */
  key: 'passwordDeadlineDays' | 'passwordDeadlineOneDay' | 'passwordDeadlineToday' | 'passwordDeadlinePassed';
  days: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** `YYYY-MM-DD` as a local calendar date, or null when it isn't one. */
export function parseDeadline(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
  if (!match) {
    return null;
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const date = new Date(year, month, day);
  return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day ? date : null;
}

/**
 * Whole days left until the end of the deadline day, in local time: 0 on the day itself, negative once it has passed.
 * Counted in calendar days, so a daylight-saving change in between doesn't shift it.
 */
export function daysLeft(deadline: Date, now: Date = new Date()): number {
  const end = Date.UTC(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((end - today) / DAY_MS);
}

/** The line to show for a deadline, or null when there is none. */
export function deadlineNotice(value: string | null | undefined, now: Date = new Date()): DeadlineNotice | null {
  const deadline = parseDeadline(value);
  if (!deadline) {
    return null;
  }
  const days = daysLeft(deadline, now);
  const key = days > 1 ? 'passwordDeadlineDays' : days === 1 ? 'passwordDeadlineOneDay' : days === 0 ? 'passwordDeadlineToday' : 'passwordDeadlinePassed';
  return { key, days };
}

/** The deadline out of the SetPassword announcement's payload (`{"deadline":"YYYY-MM-DD"}`), or null. */
export function deadlineFromPayload(payload: string | null | undefined): string | null {
  if (!payload) {
    return null;
  }
  try {
    const deadline = JSON.parse(payload)?.deadline;
    return typeof deadline === 'string' ? deadline : null;
  } catch {
    return null;
  }
}

/** The deadline as the user reads dates, e.g. "15 November 2026" (en-GB) or "15 novembre 2026". */
export function formatDeadline(deadline: Date, lang: string | null | undefined): string {
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
  try {
    return new Intl.DateTimeFormat(lang || 'en-GB', options).format(deadline);
  } catch {
    return new Intl.DateTimeFormat('en-GB', options).format(deadline);
  }
}
